import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import {
  GbgrundbuchgeschaeftService,
  GrundbuchgeschaeftListEntryMeta,
  GrundstueckId,
  LoadGbGeschaeftSichtByGrundstueckIdsRequest
} from '../../../../core/gb-api';
import { AbstractComponent } from '../../abstract-component/abstract.component';
import { ListLoader } from '../../list-loader/list-loader';
import { ListLoaderPaginationBuilder } from '../../list-loader/list-loader-pagination';
import { map } from 'rxjs/operators';
import { PaginationLookupResult } from '../../list-loader/pagination-lookup-result';
import { SortDirection, TableSortModel } from '../../../tablesort/tableSortModel';
import { PersonStammMasterDetailEntryMeta, PersonstammpersonService } from '../../../../core/personstamm-api';
import { HistoryMode } from '../../../../core/services/HistoryMode';
import { TimepointService } from '../../../../core/services/timepoint.service';
import { ComparatorService } from '../../../services/comparator-service';
import { Subscription } from 'rxjs';
import { FormatConfigService } from '../../../../core/services/format-config.service';
import { ErweiterungConfigResponse, PermissionResponse } from '../../../../core/base-api';
import { ListLoaderWrapperComponent } from '../../list-loader-wrapper/list-loader-wrapper.component';
import { NgTemplateOutlet } from '@angular/common';
import { ColumnHeaderComponent } from '../../../tablesort/sort-icon/column-header.component';
import { KompaktGrundbuchgeschaeftComponent } from '../../../kompaktdarstellung/grundbuchgeschaeft/kompakt-grundbuchgeschaeft.component';
import { LinkComponent } from '../../link/link.component';
import { KompaktStammpersonComponent } from '../../../kompaktdarstellung/stammperson/kompakt-stammperson.component';
import { TranslatePipe } from '@ngx-translate/core';
import { DatumPipe } from '../../../pipes/simple/datum.pipe';
import { TooltipPersonstammPipe } from '../../../pipes/simple/tooltip-personstamm.pipe';
import { AppPermissionService } from '../../../../core/services/app-permission.service';
import SortEnum = LoadGbGeschaeftSichtByGrundstueckIdsRequest.SortEnum;
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;
import PermissionsEnum = PermissionResponse.PermissionsEnum;

@Component({
  selector: 'cap-haengige-gb-geschaefte',
  templateUrl: './haengige-gb-geschaefte.component.html',
  styleUrls: ['./haengige-gb-geschaefte.component.scss'],
  imports: [
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    ColumnHeaderComponent,
    KompaktGrundbuchgeschaeftComponent,
    LinkComponent,
    KompaktStammpersonComponent,
    TranslatePipe,
    DatumPipe,
    TooltipPersonstammPipe
  ]
})
export class HaengigeGbGeschaefteComponent extends AbstractComponent implements OnDestroy, OnChanges {
  @Input()
  perimeter: Array<GrundstueckId>;
  @Input()
  hasRight = true;

  haengigeLoader: ListLoader<GrundbuchgeschaeftListEntryVM>;
  tableSortModel = new TableSortModel<GrundbuchgeschaeftListEntryVM>('table', this.comparatorService);
  public isErweiterungGeschaeftsModulActive: boolean;
  hasPermissionAuskunft: boolean;
  private readonly PAGE_SIZE = 5;
  private readonly CURRENT_PAGE = 1;

  constructor(
    private grundbuchgeschaeftService: GbgrundbuchgeschaeftService,
    private personstammService: PersonstammpersonService,
    private zeitpunktService: TimepointService,
    private comparatorService: ComparatorService,
    private appformatConfigService: FormatConfigService,
    private appPermissionService: AppPermissionService
  ) {
    super();
    this.tableSortModel.addColumn('tagebuchnummer', undefined, SortEnum.TAGEBUCHNUMMER).changeSortMode(SortDirection.DESC);
    this.tableSortModel.addColumn('status', undefined, SortEnum.STATUS);
    this.tableSortModel.addColumn('eingangTagebuch', undefined, SortEnum.EINGANG_TAGEBUCH);
    this.addSub(
      this.appformatConfigService.erweiterungsConfig$.subscribe((config) => {
        this.isErweiterungGeschaeftsModulActive = config?.activeErweiterungen.includes(ActiveErweiterungenEnum.GESCHAEFTSMODUL_CAP_WEB);
      })
    );
    this.hasPermissionAuskunft = this.appPermissionService.hasPermission(PermissionsEnum.AUSKUNFTSPERRE);
  }

  _loadOption;

  @Input()
  set loadOption(value: string) {
    if (!value) {
      return;
    }
    this._loadOption = value;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.perimeter?.currentValue || this.perimeter) {
      this.updateLoader();
    }
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    if (this.haengigeLoader) {
      this.haengigeLoader.unsubscribeAll();
    }
  }

  hasAuskunftsperre(auskunftsperre: PersonStammMasterDetailEntryMeta.AuskunftsperreEnum): boolean {
    return auskunftsperre === PersonStammMasterDetailEntryMeta.AuskunftsperreEnum.AUSKUNFT_SPERRE;
  }

  showPersonendaten(auskunftsperre: PersonStammMasterDetailEntryMeta.AuskunftsperreEnum): boolean {
    let hasAuskunftsperre = this.hasAuskunftsperre(auskunftsperre);
    return !hasAuskunftsperre || (hasAuskunftsperre && this.hasPermissionAuskunft);
  }

  private updateLoader() {
    if (!this.hasRight || !this.perimeter) {
      return;
    }
    if (this.haengigeLoader) {
      this.haengigeLoader.unsubscribeAll();
    }
    if (this.perimeter.length === 1) {
      this.haengigeLoader = this.createGbGeschaeftByGrundstueckIdLoader();
    } else {
      this.haengigeLoader = this.createGbGeschaeftByGrundstueckIdsLoader();
    }
    this.haengigeLoader.applySortModel(this.tableSortModel);
  }

  private createGbGeschaeftByGrundstueckIdLoader(): ListLoader<GrundbuchgeschaeftListEntryVM> {
    const observable = (offset, limit, sortColumn) => this.createGbGeschaeftByGrundsuteckIdObservable(offset, limit, sortColumn);
    const loader = ListLoaderPaginationBuilder.builder<GrundbuchgeschaeftListEntryVM>()
      .setLookupItemFunc(observable)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPageSize(this.PAGE_SIZE)
      .build();
    this.addLoader(loader);
    return loader;
  }

  private createGbGeschaeftByGrundsuteckIdObservable(offset, limit, sortColumn) {
    return this.grundbuchgeschaeftService
      .loadGbGeschaeftSichtGrundstueck(
        this.perimeter[0].id,
        offset,
        limit,
        sortColumn.sortDirectionBackend,
        sortColumn.sortEnumBackend,
        this._loadOption
      )
      .pipe(
        map((r) => {
          const geschaefteVMs: Array<GrundbuchgeschaeftListEntryVM> = [];
          r.results.forEach((entry) =>
            geschaefteVMs.push(new GrundbuchgeschaeftListEntryVM(entry, this.getViewModelCtx(), (sub: Subscription) => this.addSub(sub)))
          );
          return <PaginationLookupResult<GrundbuchgeschaeftListEntryVM>>{
            result: geschaefteVMs,
            totalItems: r.totalItems
          };
        })
      );
  }

  private createGbGeschaeftByGrundstueckIdsLoader(): ListLoader<GrundbuchgeschaeftListEntryVM> {
    const observable = (offset, limit, sortColumn) => this.createGbGeschaeftByGrundsuteckIdsObservable(offset, limit, sortColumn);

    const loader = ListLoaderPaginationBuilder.builder<GrundbuchgeschaeftListEntryVM>()
      .setLookupItemFunc(observable)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPageSize(this.PAGE_SIZE)
      .build();
    this.addLoader(loader);
    return loader;
  }

  private createGbGeschaeftByGrundsuteckIdsObservable(offset, limit, sortColumn) {
    const request: LoadGbGeschaeftSichtByGrundstueckIdsRequest = {
      grundstueckIds: this.perimeter,
      auftragLoadOption: 'HAENGIGE',
      offset: offset,
      limit: limit,
      sortDirection: sortColumn.sortDirectionBackend,
      sort: sortColumn.sortEnumBackend
    };
    return this.grundbuchgeschaeftService.loadGbGeschaeftSichtGrundstuecke(request).pipe(
      map((r) => {
        const geschaefteVMs: Array<GrundbuchgeschaeftListEntryVM> = [];
        r.results.forEach((entry) =>
          geschaefteVMs.push(new GrundbuchgeschaeftListEntryVM(entry, this.getViewModelCtx(), (sub) => this.addSub(sub)))
        );
        return <PaginationLookupResult<GrundbuchgeschaeftListEntryVM>>{
          result: geschaefteVMs,
          totalItems: r.totalItems
        };
      })
    );
  }

  private getViewModelCtx() {
    return <ViewModelCtx>{
      personstammService: this.personstammService,
      zeitpunktService: this.zeitpunktService
    };
  }
}

interface ViewModelCtx {
  personstammService: PersonstammpersonService;
  zeitpunktService: TimepointService;
}

class GrundbuchgeschaeftListEntryVM {
  public anmelder: Array<PersonStammMasterDetailEntryMeta>;

  constructor(
    public object: GrundbuchgeschaeftListEntryMeta,
    private ctx: ViewModelCtx,
    private addSub: (sub: Subscription) => void
  ) {
    this.loadAnmelder();
  }

  loadAnmelder() {
    if (!this.object.anmelderId) {
      return;
    }
    this.anmelder = [];
    for (const person of this.object.anmelderId) {
      this.addSub(
        this.ctx.personstammService
          .loadById(
            person,
            HistoryMode.HistoryMode.OHNE_HIST_MIT_GESCHAEFTSAENDERUNGEN,
            null,
            this.ctx.zeitpunktService.timepointCtx.timepointIso,
            this.ctx.zeitpunktService.timepointCtx.timepointId
          )
          .subscribe((entry) => this.anmelder.push(entry))
      );
    }
  }
}
